import { useEffect, useState } from "react";
import { Map, Globe, Smartphone, Apple, Server, Loader2, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";

const SLOTS = [
  { key: "web", label: "Web (browser app)", icon: Globe, hint: "Active now — served from the app's environment. Restrict to your web domains in Google Cloud." },
  { key: "android", label: "Android (native app)", icon: Smartphone, hint: "Placeholder — paste a key restricted to your Android package + SHA-1 when you ship a native app." },
  { key: "ios", label: "iOS (native app)", icon: Apple, hint: "Placeholder — paste a key restricted to your iOS bundle id when you ship a native app." },
  { key: "backend", label: "Backend (server-side)", icon: Server, hint: "Placeholder — a separate IP-restricted key for any server-side Google API calls." },
];

export default function AdminMapsKeys() {
  const [slots, setSlots] = useState({});
  const [vals, setVals] = useState({ web: "", android: "", ios: "", backend: "" });
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/maps-keys").then((r) => setSlots(r.data.slots || {})).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = Object.fromEntries(Object.entries(vals).filter(([, v]) => v.trim()));
    if (Object.keys(body).length === 0) { toast.error("Paste at least one key to save."); return; }
    setSaving(true);
    try { await api.put("/admin/maps-keys", body); toast.success("Map keys saved"); setVals({ web: "", android: "", ios: "", backend: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-maps-keys">
      <PageHeader title="Google Maps Keys" icon={Map} subtitle="Separate, restricted key slots per platform. The web key powers the app today; Android, iOS and backend are placeholders for native builds." />
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
        <ShieldCheck className="mr-1 inline h-4 w-4" /> Keys are stored securely and never shown back. Restrict every key in Google Cloud (by domain / app id / IP) before going live. Enable Maps JavaScript, Places and Directions APIs.
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {SLOTS.map(({ key, label, icon: Icon, hint }) => {
          const s = slots[key] || {};
          return (
            <div key={key} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`maps-slot-${key}`}>
              <div className="flex items-center justify-between">
                <p className="flex items-center gap-2 font-semibold text-slate-800"><Icon className="h-4 w-4 text-sky-600" /> {label}</p>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${s.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`maps-status-${key}`}>
                  {s.active ? (s.source === "env" ? "Active (env)" : "Configured") : "Empty"}
                </span>
              </div>
              <p className="mt-1 text-[11px] text-slate-400">{hint}</p>
              <input type="password" value={vals[key]} onChange={(e) => setVals((v) => ({ ...v, [key]: e.target.value }))}
                placeholder={s.active ? "Paste to replace…" : "Paste API key…"} data-testid={`maps-input-${key}`}
                className="mt-2 h-9 w-full rounded-md border border-slate-200 px-2 text-sm outline-none focus:border-sky-400" />
            </div>
          );
        })}
      </div>
      <button onClick={save} disabled={saving} data-testid="maps-keys-save" className="mt-4 inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save keys
      </button>
    </div>
  );
}
